import { useCallback, useEffect } from 'react';
import { useFocusEffect, useLocalSearchParams, useNavigation, useRouter } from 'expo-router';

import { useHeaderButtonOptions } from '../../../../components';
import { handleHardwareBackButtonPress } from '../../../../navigation';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small hook to conditionally render custom navigation header buttons.
 *
 * NOTE: We use custom back button navigation since this screen can be
 *       accessed from the gift card orders screen, which is located in a
 *       different stack, so the default `goBack` function won't work
 *       as expected.
 */
export const useGiftCardConfirmationHeaderButtons = (params: Params) => {
  const { isModal } = params;

  // ─── Route Params ─────────────────────────────────────────────────────

  const { referrer } = useLocalSearchParams<{ referrer?: string }>();

  // ─── Navigation ───────────────────────────────────────────────────────

  const navigation = useNavigation();
  const router = useRouter();

  const shouldShowGiftCardOrdersBackButton = referrer === 'gift-card-orders';

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToGiftCardsScreen = useCallback(() => {
    router.dismissTo('/account/gift');
  }, [router]);

  const navigateToGiftCardOrdersScreen = useCallback(() => {
    router.navigate('/account/gift-cards');
  }, [router]);

  const navigateToPreviousScreen = useCallback(() => {
    if (shouldShowGiftCardOrdersBackButton) {
      navigateToGiftCardOrdersScreen();

      return;
    }

    navigateToGiftCardsScreen();
  }, [
    navigateToGiftCardOrdersScreen,
    navigateToGiftCardsScreen,
    shouldShowGiftCardOrdersBackButton,
  ]);

  // The modal version keeps its stack's own close and back behavior.
  const overrideHardwareBackButtonPress = useCallback(() => {
    if (isModal) {
      return;
    }

    return handleHardwareBackButtonPress(() => {
      navigateToPreviousScreen();

      return true;
    });
  }, [isModal, navigateToPreviousScreen]);

  // ─── Header Controls ─────────────────────────────────────────────────

  const { headerLeftOptions } = useHeaderButtonOptions({ headerLeft: renderNoHeaderLeft });

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(overrideHardwareBackButtonPress);

  /**
   * Set header controls.
   */
  useEffect(() => {
    if (isModal) {
      return;
    }

    const setOptions = navigation.setOptions;

    setOptions({
      ...headerLeftOptions,

      // NOTE: We use a custom back button, thus we need to hide the default one.
      headerBackVisible: false,
    });
  }, [headerLeftOptions, isModal, navigation]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A no-op headerLeft renderer that prevents the default `HeaderBackButton`
 * from mounting.
 */
function renderNoHeaderLeft() {
  return null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  isModal: boolean;
};
